import { Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, Self } from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { applicationDebounceTime } from '@anduin/shared';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-input-with-variable',
  templateUrl: './input-with-variable.component.html',
  styleUrls: ['./input-with-variable.component.scss'],
  standalone: false,
})
export class InputWithVariableComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private _unsubscribe: Subject<void> = new Subject<void>();
  onChange: any;
  onTouch: any;
  _idSuffix = `${Math.random() * 100}`;
  _variables$: BehaviorSubject<Map<string, string>> = new BehaviorSubject<Map<string, string>>(new Map());
  variables$: Observable<[string, string][]> = this._variables$.asObservable().pipe(map((vars) => [...vars.entries()]));
  isDisabled = false;

  @Input() label: string;
  @Input() hint: string;
  @Input() inputType: 'input' | 'textarea' = 'input';
  @Input() placeholder: string;

  @Input() set value(newValue: string) {
    this.innerControl.setValue(newValue, { emitEvent: false });
  }

  @Input() set variables(newVariables: Map<string, string>) {
    this._variables$.next(newVariables);
  }

  @Output() isValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  innerControl: UntypedFormControl = this.builder.control('', (control) =>
    control.value &&
    `${control.value}`
      .replace(new RegExp(`\\$\\{(${[...this._variables$.value.values()].join('|')})\\}`, 'g'), '')
      .match(new RegExp('\\$\\{.*\\}'))
      ? { variable: 'One or more variables do not match the provided options' }
      : null,
  );

  constructor(
    private readonly builder: UntypedFormBuilder,
    @Self() @Optional() public parentControl: NgControl,
  ) {
    this.parentControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.innerControl.valueChanges
      .pipe(
        applicationDebounceTime(),
        tap((value) => this.onChange(value)),
        takeUntil(this._unsubscribe),
      )
      .subscribe();

    this.innerControl.valueChanges
      .pipe(
        tap((value) => this.onTouch()),
        take(1),
        takeUntil(this._unsubscribe),
      )
      .subscribe();

    this.innerControl.valueChanges
      .pipe(
        map((value) => this.innerControl.valid),
        distinctUntilChanged(),
        tap((isValid) => this.isValid.emit(isValid)),
        takeUntil(this._unsubscribe),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    this.innerControl.setValue(obj, { emitEvent: false });
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.innerControl.disable();
    } else {
      this.innerControl.enable();
    }
  }

  appendVariable(variable: string): void {
    this.innerControl.setValue(`${this.innerControl.value ? this.innerControl.value : ''}\${${variable}}`);
  }
}
